import { useState, useCallback, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { useDebounce } from '@/shared/hooks'
import {
  Search, X, Phone, Mail, Calendar, Eye, CheckCircle,
  XCircle, ChevronLeft, ChevronRight, Camera, CreditCard, User, FileText
} from 'lucide-react'
import { cn } from '@/shared/utils'

// ─── Types ────────────────────────────────────────────────────────────────────
type StatusTab = 'pending' | 'approved' | 'rejected'

const STATUS_TABS: { value: StatusTab; label: string; color: string }[] = [
  { value: 'pending',  label: 'Pending Profile Picture Update Request',  color: '#f68b2c' },
  { value: 'approved', label: 'Approved Profile Picture Update Request', color: '#16a34a' },
  { value: 'rejected', label: 'Rejected Profile Picture Update Request', color: '#dc2626' },
]

// Same status derivation used for each card's badge — reused here so the list
// can be filtered client-side, since the backend's `action` filter only ever
// separates approve/reject and returns pending + resolved rows mixed together.
// `is_verified` is a STRING on the real response ("Approved" / "Pending" /
// "Rejected"), not the 0/1 flag this originally assumed — confirmed via a live
// admin/trainerProfileupdate/get-list response.
function deriveStatus(row: any): 'pending' | 'approved' | 'rejected' {
  const v = String(row?.is_verified ?? '').toLowerCase()
  if (v === 'approved') return 'approved'
  if (v === 'rejected') return 'rejected'
  return 'pending'
}

// The API's created_at comes back as "2026-09-20 22:42:53" — a space, not the
// "T" ISO needs — so `new Date(...)` on it as-is returns Invalid Date in
// Safari/Firefox (Chrome is lenient about it, which is why this went unnoticed).
function fmtCreatedAt(iso?: string | null) {
  if (!iso) return '—'
  const d = new Date(String(iso).replace(' ', 'T'))
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}


// ─── Status chip ──────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    pending:  'bg-brand-500 text-white',
    approved: 'bg-green-500 text-white',
    rejected: 'bg-red-500 text-white',
  }
  return (
    <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-bold capitalize', map[status] ?? 'bg-gray-200 text-gray-700')}>
      {status}
    </span>
  )
}

// ─── Modal: Photo comparison ──────────────────────────────────────────────────
function PhotoModal({ row, onClose, onApprove, onReject, saving }: any) {
  const fields = row?.updated_fields ?? {}
  let oldPhoto = '', newPhoto = ''
  for (const section of Object.values(fields) as any[]) {
    if (section?.photo_url) { oldPhoto = section.photo_url.old ?? ''; newPhoto = section.photo_url.new ?? '' }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.5)'}}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900">Updated Photo</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center"><X size={16}/></button>
        </div>
        <div className="px-6 py-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-full bg-brand-500 flex items-center justify-center text-white font-bold text-sm">
              {row?.user?.name?.[0]?.toUpperCase() ?? 'T'}
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">{row?.user?.name ?? '—'}</p>
              <p className="text-xs text-gray-400">Trainer ID: #{row?.user_id}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[{label:'OLD PHOTO', src:oldPhoto}, {label:'UPDATED PHOTO', src:newPhoto}].map(({label,src})=>(
              <div key={label} className="rounded-xl overflow-hidden border border-gray-200">
                {src ? (
                  <img src={src} alt={label} className="w-full h-48 object-cover"/>
                ) : (
                  <div className="w-full h-48 bg-gray-100 flex items-center justify-center text-gray-400 text-xs">No photo</div>
                )}
                <div className="py-2 text-center text-xs font-bold tracking-wide"
                  style={{background: label.includes('OLD') ? '#f68b2c' : '#16a34a', color:'white'}}>
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onReject} disabled={saving}
            className="py-2.5 rounded-xl border-2 border-red-400 text-red-500 text-sm font-bold hover:bg-red-50 disabled:opacity-50">
            Reject
          </button>
          <button onClick={onApprove} disabled={saving}
            className="py-2.5 rounded-xl border-2 border-green-400 text-green-600 text-sm font-bold hover:bg-green-50 disabled:opacity-50 bg-green-50">
            {saving ? 'Saving...' : 'Accept'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Modal: Phone comparison (read-only log — only photo changes are actionable) ──
function PhoneModal({ row, onClose }: any) {
  const fields = row?.updated_fields ?? {}
  let oldPhone = '', newPhone = ''
  for (const section of Object.values(fields) as any[]) {
    if (section?.phone) { oldPhone = section.phone.old ?? ''; newPhone = section.phone.new ?? '' }
    if (section?.country_code) { /* ignore */ }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.5)'}}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900">Updated Phone Number</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center"><X size={16}/></button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-full bg-brand-500 flex items-center justify-center text-white font-bold text-sm">
              {row?.user?.name?.[0]?.toUpperCase() ?? 'T'}
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">{row?.user?.name ?? '—'}</p>
              <p className="text-xs text-gray-400">Trainer ID: #{row?.user_id}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[{label:'OLD PHONE NUMBER', val: oldPhone ? `+971 ${oldPhone}` : oldPhone}, {label:'UPDATED PHONE NUMBER', val: newPhone ? `+971 ${newPhone}` : newPhone}].map(({label,val})=>(
              <div key={label}>
                <p className="text-[12px] font-bold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
                <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-3">
                  <Phone size={14} className="text-gray-400 flex-shrink-0"/>
                  <span className="text-sm font-semibold text-gray-800">{val || '—'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-100">
          <button onClick={onClose}
            className="w-full py-2.5 rounded-xl border-2 border-gray-200 text-gray-600 text-sm font-bold hover:bg-gray-50">
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Modal: Bank Account comparison (read-only log — only photo changes are actionable) ──
function BankModal({ row, onClose }: any) {
  const fields = row?.updated_fields ?? {}
  const bankSection: any = (Object.values(fields) as any[]).find((s: any) => s?.bank_account_number || s?.bank_name || s?.iban) ?? {}

  const oldBank = {
    holder: bankSection?.account_holder_name?.old ?? '—',
    number: bankSection?.bank_account_number?.old ?? '—',
    bank:   bankSection?.bank_name?.old ?? '—',
    iban:   bankSection?.iban?.old ?? '—',
  }
  const newBank = {
    holder: bankSection?.account_holder_name?.new ?? '—',
    number: bankSection?.bank_account_number?.new ?? '—',
    bank:   bankSection?.bank_name?.new ?? '—',
    iban:   bankSection?.iban?.new ?? '—',
  }

  const BankInfo = ({ label, data }: any) => (
    <div>
      <p className="text-[12px] font-bold text-gray-400 uppercase tracking-wide mb-3">{label}</p>
      <div className="space-y-3">
        {[
          { icon: <User size={14}/>, label:'Account Holder Name', val: data.holder },
          { icon: <CreditCard size={14}/>, label:'Account Number',  val: data.number },
          { icon: <User size={14}/>, label:'Bank Name',            val: data.bank },
          { icon: <CreditCard size={14}/>, label:'IBAN',           val: data.iban },
        ].map(({icon,label,val})=>(
          <div key={label}>
            <p className="text-[11px] text-gray-400 uppercase tracking-widest mb-0.5">{label}</p>
            <div className="flex items-center gap-2">
              <span className="text-gray-400">{icon}</span>
              <span className="text-sm font-semibold text-gray-800">{val}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.5)'}}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900">Updated Bank Account</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center"><X size={16}/></button>
        </div>
        <div className="px-6 py-4">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-full bg-brand-500 flex items-center justify-center text-white font-bold text-sm">
              {row?.user?.name?.[0]?.toUpperCase() ?? 'T'}
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">{row?.user?.name ?? '—'}</p>
              <p className="text-xs text-gray-400">Trainer ID: #{row?.user_id}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6 border-t border-gray-100 pt-4">
            <BankInfo label="Old Bank Account Info" data={oldBank}/>
            <BankInfo label="Updated Bank Account Info" data={newBank}/>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-100">
          <button onClick={onClose}
            className="w-full py-2.5 rounded-xl border-2 border-gray-200 text-gray-600 text-sm font-bold hover:bg-gray-50">
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Detect update type from updated_fields ───────────────────────────────────
function detectUpdateType(row: any): 'photo' | 'phone' | 'bank' | 'other' {
  const fields = row?.updated_fields ?? {}
  for (const section of Object.values(fields) as any[]) {
    if (!section) continue
    if (section.photo_url) return 'photo'
    if (section.phone)     return 'phone'
    if (section.bank_account_number || section.bank_name || section.iban || section.account_holder_name) return 'bank'
  }
  return 'other'
}

// ─── What changed on a single request, tagged by type so a merged card can
// route each line's "View Details" to the right modal / route photo lines to
// Accept-Reject while everything else stays a read-only log line ────────────
type ChangeItem = { type: 'photo' | 'phone' | 'bank' | 'other'; label: string }
function getChangeItems(row: any): ChangeItem[] {
  const fields = row?.updated_fields ?? {}
  const items: ChangeItem[] = []
  for (const section of Object.values(fields) as any[]) {
    if (!section) continue
    for (const key of Object.keys(section)) {
      if (key === 'photo_url')        items.push({ type: 'photo', label: 'Requested New Photo' })
      else if (key === 'phone')       items.push({ type: 'phone', label: 'Update Phone Number' })
      else if (key === 'bank_account_number' || key === 'bank_name' || key === 'iban' || key === 'account_holder_name')
        items.push({ type: 'bank', label: 'Update Bank Account Details' })
      else if (key === 'is_pickup_enabled' || key === 'pickup_enabled') items.push({ type: 'other', label: 'Enabled Pickup Option' })
      else if (key === 'max_radius_in_km') {
        const old = section[key]?.old ?? ''; const nw = section[key]?.new ?? ''
        items.push({ type: 'other', label: `Max Radius in KM Changed From ${old} to ${nw}` })
      } else if (key === 'gear') {
        const old = section[key]?.old ?? ''; const nw = section[key]?.new ?? ''
        items.push({ type: 'other', label: `GEAR Changed from ${old} to ${nw}` })
      } else {
        const old = section[key]?.old ?? ''; const nw = section[key]?.new ?? ''
        items.push({ type: 'other', label: `${key.replace(/_/g,' ')} changed${old ? ` from ${old}` : ''}${nw ? ` to ${nw}` : ''}` })
      }
    }
  }
  return items
}

// ─── Group a trainer's separate profile-update requests into one card. Only a
// photo change is actionable (Accept/Reject); everything else is a log line,
// so the group only needs to track whichever request carries the photo change
// for its status/id — the rest just contribute display-only change items ────
interface TrainerGroup {
  userId: string
  user: any
  requests: any[]
  photoRequest: any | null
  items: ChangeItem[]
  latestCreatedAt: string | null
}
function groupByTrainer(rows: any[]): TrainerGroup[] {
  const map = new Map<string, TrainerGroup>()
  for (const row of rows) {
    const key = String(row.user_id ?? row.user?.id ?? row.id)
    let g = map.get(key)
    if (!g) {
      g = { userId: key, user: row.user, requests: [], photoRequest: null, items: [], latestCreatedAt: null }
      map.set(key, g)
    }
    g.requests.push(row)
    if (!g.user && row.user) g.user = row.user
    if (!g.photoRequest && detectUpdateType(row) === 'photo') g.photoRequest = row
    if (row.created_at && (!g.latestCreatedAt || row.created_at > g.latestCreatedAt)) g.latestCreatedAt = row.created_at
    g.items.push(...getChangeItems(row))
  }
  // Dedup identical log lines (e.g. the same unresolved phone change appearing
  // on more than one request for the trainer).
  for (const g of map.values()) {
    g.items = Array.from(new Map(g.items.map(i => [i.label, i])).values())
  }
  return Array.from(map.values())
}

// ─── Single trainer card — one per trainer, merging every one of their pending
// requests. Only the photo change (if any) is actionable; everything else is a
// read-only log line ───────────────────────────────────────────────────────────
function TrainerGroupCard({ group, onReload }: { group: TrainerGroup; onReload: () => void }) {
  const navigate   = useNavigate()
  const [modal,    setModal]   = useState<'photo'|'phone'|'bank'|null>(null)
  const [saving,   setSaving]  = useState(false)
  const [toast,    setToast]   = useState<{msg:string; type:'ok'|'err'} | null>(null)

  const photoRequest = group.photoRequest
  const status     = photoRequest ? deriveStatus(photoRequest) : 'pending'
  const isApproved = status === 'approved'
  const isRejected = status === 'rejected'
  const isPending  = !isApproved && !isRejected
  const user       = group.user

  const phoneRow = group.requests.find(r => (Object.values(r?.updated_fields ?? {}) as any[]).some(s => s?.phone))
  const bankRow  = group.requests.find(r => (Object.values(r?.updated_fields ?? {}) as any[])
    .some(s => s?.bank_account_number || s?.bank_name || s?.iban || s?.account_holder_name))

  const showToast = (msg: string, type: 'ok'|'err') => {
    setToast({msg, type})
    setTimeout(() => setToast(null), 3000)
  }

  const callAction = useCallback(async (action: 'approve' | 'reject') => {
    if (!photoRequest) return
    setSaving(true)
    try {
      const res = await restClient.post(endpoints.profileUpdate.approveReject, { id: photoRequest.id, action })
      showToast(res.data?.message ?? 'Done', 'ok')
      setModal(null)
      onReload()
    } catch(e: any) {
      const errs = e?.response?.data?.errors
      const msg  = errs ? Object.values(errs).flat().join(', ') : (e?.response?.data?.message ?? 'Failed')
      showToast(msg, 'err')
    } finally { setSaving(false) }
  }, [photoRequest, onReload])

  const openViewModal = (type: ChangeItem['type']) => {
    if (type === 'photo') setModal('photo')
    else if (type === 'phone') setModal('phone')
    else if (type === 'bank') setModal('bank')
  }

  const cardBg = isApproved ? '#f0fdf4' : isRejected ? '#fff5f5' : '#fff'
  const cardBorder = isApproved ? '#bbf7d0' : isRejected ? '#fecaca' : '#e5e7eb'

  return (
    <>
      {toast && (
        <div className={cn('fixed top-4 right-4 z-[100] px-4 py-3 rounded-xl shadow-lg text-sm font-semibold text-white',
          toast.type === 'ok' ? 'bg-green-500' : 'bg-red-500')}>
          {toast.msg}
        </div>
      )}

      <div className="rounded-2xl border shadow-sm overflow-hidden" style={{background:cardBg, borderColor:cardBorder}}>
        {/* Header */}
        <div className="flex items-start justify-between px-4 pt-4 pb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
              style={{background: isApproved ? '#16a34a' : isRejected ? '#dc2626' : '#f68b2c'}}>
              {user?.name?.[0]?.toUpperCase() ?? 'T'}
            </div>
            <div>
              <button onClick={() => navigate(`/dashboard/user/${group.userId}`)}
                className="text-sm font-bold text-gray-900 hover:underline text-left">
                {user?.name ?? '—'}
              </button>
              <p className="text-xs text-gray-400">Trainer ID: #{group.userId}</p>
            </div>
          </div>
          <StatusBadge status={status}/>
        </div>

        {/* Contact info */}
        <div className="px-4 pb-2 space-y-1">
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1"><Mail size={11}/>{user?.email ?? '—'}</span>
            <span className="flex items-center gap-1"><Phone size={11}/>+{user?.country_code ?? '971'} {user?.phone ?? '—'}</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <Calendar size={11}/>
            {fmtCreatedAt(group.latestCreatedAt)}
          </div>
        </div>

        {/* Change log */}
        <div className="px-4 pb-3 space-y-1.5">
          {group.items.map((item, i) => (
            <div key={i} className="flex items-center justify-between gap-2 border rounded-lg px-3 py-2"
              style={{borderColor:cardBorder, background: item.type === 'photo' ? (isApproved ? '#dcfce7' : isRejected ? '#fee2e2' : '#fff7ed') : '#f9fafb'}}>
              <div className="flex items-center gap-2 text-xs font-medium text-gray-700 min-w-0">
                <span style={{color: item.type === 'photo' ? '#f68b2c' : '#9ca3af'}} className="flex-shrink-0">ℹ</span>
                <span className="truncate">{item.label}</span>
              </div>
              {(item.type === 'photo' || item.type === 'phone' || item.type === 'bank') && (
                <button onClick={() => openViewModal(item.type)}
                  className="text-xs font-semibold text-green-600 hover:underline flex-shrink-0 flex items-center gap-1">
                  <Eye size={11}/>
                  {item.type === 'photo' ? 'View Photo' : 'View Details'}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Action buttons — photo verification only; everything else is a log */}
        {photoRequest && (
          <div className="px-4 pb-4">
            {isPending && (
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => callAction('reject')} disabled={saving}
                  className="py-2 rounded-xl border-2 border-red-300 text-red-500 text-xs font-bold hover:bg-red-50 disabled:opacity-50">
                  Reject
                </button>
                <button onClick={() => callAction('approve')} disabled={saving}
                  className="py-2 rounded-xl border-2 border-green-300 text-green-600 text-xs font-bold hover:bg-green-50 disabled:opacity-50">
                  Accept
                </button>
              </div>
            )}
            {isApproved && (
              <button disabled className="w-full py-2 rounded-xl border-2 border-green-300 text-green-600 text-xs font-bold bg-green-50 cursor-default">
                Accepted
              </button>
            )}
            {isRejected && (
              <button disabled className="w-full py-2 rounded-xl border-2 border-red-300 text-red-400 text-xs font-bold bg-red-50 cursor-default flex items-center justify-center gap-1">
                <XCircle size={13}/> Not professional
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {modal === 'photo' && photoRequest && (
        <PhotoModal row={photoRequest} onClose={() => setModal(null)}
          onApprove={() => callAction('approve')} onReject={() => callAction('reject')} saving={saving}/>
      )}
      {modal === 'phone' && phoneRow && (
        <PhoneModal row={phoneRow} onClose={() => setModal(null)}/>
      )}
      {modal === 'bank' && bankRow && (
        <BankModal row={bankRow} onClose={() => setModal(null)}/>
      )}
    </>
  )
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
// Pending/Approved/Rejected are separate routes (…/pending, …/approved,
// …/rejected) rather than in-page tabs, so each status is a fully independent,
// bookmarkable/shareable page load — not a shared view toggled by local state.
export function TrainerProfileUpdatesPage() {
  const navigate = useNavigate()
  const { status: statusParam } = useParams<{ status: string }>()
  const tab: StatusTab = (['pending','approved','rejected'] as const).includes(statusParam as StatusTab)
    ? (statusParam as StatusTab) : 'pending'
  const [search,    setSearch]   = useState('')
  const [trainerId, setTrainerId] = useState('')
  const [page,      setPage]     = useState(1)
  const [perPage,   setPerPage]  = useState(20)
  const debouncedSearch = useDebounce(search)
  const qc = useQueryClient()

  // Each status is its own page — filters don't carry over between them.
  useEffect(() => { setSearch(''); setTrainerId(''); setPage(1) }, [tab])

  // Trainers list for filter dropdown
  const { data: trainersData } = useQuery({
    queryKey: ['trainers-filter-list'],
    queryFn: () => restClient.get(`${endpoints.users.list}?user_types[]=TRAINER&limit=500&page=1`).then(r=>r.data),
    staleTime: 60_000,
  })
  const trainers: any[] = trainersData?.data ?? []

  // Status tab → action filter mapping. The backend's `action` param only ever
  // separates approve/reject — Pending sends no action so the API returns its
  // default (unresolved) queue.
  const actionFilter = tab === 'approved' ? 'approve' : tab === 'rejected' ? 'reject' : undefined

  const { data, isLoading } = useQuery({
    queryKey: ['profileUpdates', page, perPage, trainerId, actionFilter, debouncedSearch],
    queryFn: () => {
      const p = new URLSearchParams()
      p.append('page',  String(page))
      p.append('limit', String(perPage))
      if (trainerId)        p.append('trainer_id', trainerId)
      if (actionFilter)     p.append('action',     actionFilter)
      if (debouncedSearch)  p.append('search',     debouncedSearch)
      return restClient.get(`${endpoints.profileUpdate.list}?${p.toString()}`).then(r=>r.data)
    },
  })

  const rawRows: any[] = (data as any)?.data  ?? []
  const total: number = (data as any)?.total ?? 0
  const lastPage = Math.ceil(total / perPage) || 1

  // This page is the photo-verification queue only — trainers with no photo
  // change belong on the separate Change Logs page instead, and a card here
  // only ever shows its photo-change line (other changes, if any, still show
  // on the Logs page). Also acts as a safety net against the backend's
  // `action`/`search` params not being fully honored server-side, by
  // re-applying both client-side.
  const groups = groupByTrainer(rawRows)
    .filter(g => g.photoRequest)
    .filter(g => deriveStatus(g.photoRequest) === tab)
    .filter(g => !debouncedSearch || (g.user?.name ?? '').toLowerCase().includes(debouncedSearch.toLowerCase()))
    .map(g => ({ ...g, items: g.items.filter(i => i.type === 'photo') }))

  const reload = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['profileUpdates'] })
  }, [qc])

  const tabCountMap: Record<StatusTab, number | undefined> = {
    pending:  undefined,
    approved: undefined,
    rejected: undefined,
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
          <span>Dashboard</span><span>/</span><span>To-Do</span><span>/</span>
          <span className="text-brand-500 font-medium">Profile Picture Update Request</span>
        </div>
        <h1 className="text-xl font-bold text-gray-900">Profile Picture Update Request</h1>
      </div>

      {/* Filters row */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Search */}
        <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white min-w-52">
          <Search size={13} className="text-gray-400 flex-shrink-0"/>
          <input value={search} onChange={e=>{setSearch(e.target.value);setPage(1)}} placeholder="Search by name"
            className="text-xs text-gray-600 placeholder-gray-400 focus:outline-none flex-1 bg-transparent"/>
          {search && <button onClick={()=>{setSearch('');setPage(1)}}><X size={11} className="text-gray-300"/></button>}
        </div>

        {/* Trainer dropdown */}
        <select value={trainerId} onChange={e=>{setTrainerId(e.target.value);setPage(1)}}
          className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-500 min-w-44 bg-white">
          <option value="">All Trainers</option>
          {trainers.map((t:any)=><option key={t.id} value={t.id}>{t.name}</option>)}
        </select>

        {/* Reset */}
        {(search || trainerId) && (
          <button onClick={()=>{setSearch('');setTrainerId('');setPage(1)}}
            className="flex items-center gap-1 px-3 py-2 text-xs font-semibold rounded-lg border border-red-200 text-red-500 hover:bg-red-50">
            <X size={11}/> Reset
          </button>
        )}
      </div>

      {/* Status Tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        {STATUS_TABS.map(t => {
          const isActive = tab === t.value
          const count = tabCountMap[t.value]
          return (
            <button key={t.value}
              onClick={()=>navigate(`/dashboard/todo/trainer-profile-updates/${t.value}`)}
              className={cn('px-4 py-2 rounded-full text-sm font-semibold border-2 transition-all whitespace-nowrap',
                isActive ? 'text-white border-transparent' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300')}
              style={isActive ? {background: t.color, borderColor: t.color} : {}}>
              {t.label}{count !== undefined ? ` (${count})` : ''}
            </button>
          )
        })}
        <button onClick={()=>navigate('/dashboard/todo/trainer-profile-updates/logs')}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border-2 border-gray-200 bg-white text-gray-600 hover:border-gray-300 transition-all whitespace-nowrap">
          <FileText size={14}/> Change Logs
        </button>
      </div>

      {/* Grid of cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({length:6}).map((_,i)=>(
            <div key={i} className="h-72 rounded-2xl bg-gray-100 animate-pulse"/>
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <p className="text-gray-400 text-sm">No profile update requests found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map(group => (
            <TrainerGroupCard key={group.userId} group={group} onReload={reload}/>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 px-5 py-3 flex-wrap gap-3">
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <span>Items per page</span>
            <select value={perPage} onChange={e=>{setPerPage(Number(e.target.value));setPage(1)}}
              className="border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400">
              {[10,15,20,25,50].map(n=><option key={n} value={n}>{n}</option>)}
            </select>
            <span>{((page-1)*perPage)+1}–{Math.min(page*perPage,total)} of {total.toLocaleString()} items</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={()=>setPage(p=>p-1)} disabled={page<=1}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-gray-600">
              <ChevronLeft size={14}/> Previous
            </button>
            {Array.from({length:Math.min(5,lastPage)},(_,i)=>{
              const pg = Math.max(1,Math.min(page-2,lastPage-4))+i
              return (
                <button key={pg} onClick={()=>setPage(pg)}
                  className={cn('w-8 h-8 text-sm rounded-lg border font-medium',
                    pg===page?'bg-brand-500 text-white border-brand-500':'border-gray-200 hover:bg-gray-50 text-gray-600')}>
                  {pg}
                </button>
              )
            })}
            <span className="text-sm text-gray-400">of {lastPage}</span>
            <button onClick={()=>setPage(p=>p+1)} disabled={page>=lastPage}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-gray-600">
              Next <ChevronRight size={14}/>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Read-only log card — one per trainer, everything except the photo change
// (that lives on the verification queue pages instead) ─────────────────────────
function TrainerLogCard({ group, joinedAt }: { group: TrainerGroup; joinedAt?: string }) {
  const navigate = useNavigate()
  const [modal, setModal] = useState<'phone'|'bank'|null>(null)
  const user = group.user
  const items = group.items.filter(i => i.type !== 'photo')
  const phoneRow = group.requests.find(r => (Object.values(r?.updated_fields ?? {}) as any[]).some(s => s?.phone))
  const bankRow  = group.requests.find(r => (Object.values(r?.updated_fields ?? {}) as any[])
    .some(s => s?.bank_account_number || s?.bank_name || s?.iban || s?.account_holder_name))

  return (
    <>
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="flex items-start justify-between px-4 pt-4 pb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 bg-gray-400">
              {user?.name?.[0]?.toUpperCase() ?? 'T'}
            </div>
            <div>
              <button onClick={() => navigate(`/dashboard/user/${group.userId}`)}
                className="text-sm font-bold text-gray-900 hover:underline text-left">
                {user?.name ?? '—'}
              </button>
              <p className="text-xs text-gray-400">Trainer ID: #{group.userId}</p>
            </div>
          </div>
        </div>

        <div className="px-4 pb-2 space-y-1">
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1"><Mail size={11}/>{user?.email ?? '—'}</span>
            <span className="flex items-center gap-1"><Phone size={11}/>+{user?.country_code ?? '971'} {user?.phone ?? '—'}</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <Calendar size={11}/>
            Last change: {fmtCreatedAt(group.latestCreatedAt)}
          </div>
          {joinedAt && (
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <User size={11}/>
              Joined: {fmtCreatedAt(joinedAt)}
            </div>
          )}
        </div>

        <div className="px-4 pb-4 space-y-1.5">
          {items.map((item, i) => (
            <div key={i} className="flex items-center justify-between gap-2 border border-gray-100 rounded-lg px-3 py-2 bg-gray-50">
              <div className="flex items-center gap-2 text-xs font-medium text-gray-700 min-w-0">
                <span className="flex-shrink-0 text-gray-400">ℹ</span>
                <span className="truncate">{item.label}</span>
              </div>
              {(item.type === 'phone' || item.type === 'bank') && (
                <button onClick={() => setModal(item.type as 'phone'|'bank')}
                  className="text-xs font-semibold text-green-600 hover:underline flex-shrink-0 flex items-center gap-1">
                  <Eye size={11}/> View Details
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {modal === 'phone' && phoneRow && <PhoneModal row={phoneRow} onClose={() => setModal(null)}/>}
      {modal === 'bank' && bankRow && <BankModal row={bankRow} onClose={() => setModal(null)}/>}
    </>
  )
}

// ─── PAGE: Profile Change Logs — every non-photo change (phone, bank, pickup,
// gear, radius, etc.), grouped by trainer. Purely informational, no actions ────
export function TrainerProfileLogsPage() {
  const navigate = useNavigate()
  const [search,    setSearch]    = useState('')
  const [trainerId, setTrainerId] = useState('')
  const [page,      setPage]      = useState(1)
  const [perPage,   setPerPage]   = useState(20)
  const debouncedSearch = useDebounce(search)

  const { data: trainersData } = useQuery({
    queryKey: ['trainers-filter-list'],
    queryFn: () => restClient.get(`${endpoints.users.list}?user_types[]=TRAINER&limit=500&page=1`).then(r=>r.data),
    staleTime: 60_000,
  })
  const trainers: any[] = trainersData?.data ?? []
  // The profile-update rows' embedded `user` is a lean snapshot without the
  // account's own created_at — the trainer directory fetched above (for the
  // filter dropdown) already carries it, so look it up from there instead.
  const trainerJoinedAt = useMemo(
    () => new Map(trainers.map((t: any) => [String(t.id), t.created_at as string | undefined])),
    [trainers],
  )

  const { data, isLoading } = useQuery({
    queryKey: ['profileUpdateLogs', page, perPage, trainerId, debouncedSearch],
    queryFn: () => {
      const p = new URLSearchParams()
      p.append('page',  String(page))
      p.append('limit', String(perPage))
      if (trainerId)       p.append('trainer_id', trainerId)
      if (debouncedSearch) p.append('search',     debouncedSearch)
      return restClient.get(`${endpoints.profileUpdate.list}?${p.toString()}`).then(r=>r.data)
    },
  })

  const rawRows: any[] = (data as any)?.data  ?? []
  const total: number = (data as any)?.total ?? 0
  const lastPage = Math.ceil(total / perPage) || 1

  const groups = groupByTrainer(rawRows)
    .filter(g => g.items.some(i => i.type !== 'photo'))
    .filter(g => !debouncedSearch || (g.user?.name ?? '').toLowerCase().includes(debouncedSearch.toLowerCase()))

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
          <span>Dashboard</span><span>/</span><span>To-Do</span><span>/</span>
          <span className="text-brand-500 font-medium">Profile Change Logs</span>
        </div>
        <h1 className="text-xl font-bold text-gray-900">Profile Change Logs</h1>
      </div>

      {/* Filters row */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white min-w-52">
          <Search size={13} className="text-gray-400 flex-shrink-0"/>
          <input value={search} onChange={e=>{setSearch(e.target.value);setPage(1)}} placeholder="Search by name"
            className="text-xs text-gray-600 placeholder-gray-400 focus:outline-none flex-1 bg-transparent"/>
          {search && <button onClick={()=>{setSearch('');setPage(1)}}><X size={11} className="text-gray-300"/></button>}
        </div>

        <select value={trainerId} onChange={e=>{setTrainerId(e.target.value);setPage(1)}}
          className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-500 min-w-44 bg-white">
          <option value="">All Trainers</option>
          {trainers.map((t:any)=><option key={t.id} value={t.id}>{t.name}</option>)}
        </select>

        {(search || trainerId) && (
          <button onClick={()=>{setSearch('');setTrainerId('');setPage(1)}}
            className="flex items-center gap-1 px-3 py-2 text-xs font-semibold rounded-lg border border-red-200 text-red-500 hover:bg-red-50">
            <X size={11}/> Reset
          </button>
        )}
      </div>

      {/* Status Tabs (link back to the verification queue) */}
      <div className="flex items-center gap-2 flex-wrap">
        {STATUS_TABS.map(t => (
          <button key={t.value}
            onClick={()=>navigate(`/dashboard/todo/trainer-profile-updates/${t.value}`)}
            className="px-4 py-2 rounded-full text-sm font-semibold border-2 border-gray-200 bg-white text-gray-600 hover:border-gray-300 transition-all whitespace-nowrap">
            {t.label}
          </button>
        ))}
        <button className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border-2 border-transparent text-white transition-all whitespace-nowrap"
          style={{background:'#6b7280'}}>
          <FileText size={14}/> Change Logs
        </button>
      </div>

      {/* Grid of cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({length:6}).map((_,i)=>(
            <div key={i} className="h-48 rounded-2xl bg-gray-100 animate-pulse"/>
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <p className="text-gray-400 text-sm">No profile change logs found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map(group => (
            <TrainerLogCard key={group.userId} group={group} joinedAt={trainerJoinedAt.get(group.userId)}/>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 px-5 py-3 flex-wrap gap-3">
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <span>Items per page</span>
            <select value={perPage} onChange={e=>{setPerPage(Number(e.target.value));setPage(1)}}
              className="border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400">
              {[10,15,20,25,50].map(n=><option key={n} value={n}>{n}</option>)}
            </select>
            <span>{((page-1)*perPage)+1}–{Math.min(page*perPage,total)} of {total.toLocaleString()} items</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={()=>setPage(p=>p-1)} disabled={page<=1}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-gray-600">
              <ChevronLeft size={14}/> Previous
            </button>
            {Array.from({length:Math.min(5,lastPage)},(_,i)=>{
              const pg = Math.max(1,Math.min(page-2,lastPage-4))+i
              return (
                <button key={pg} onClick={()=>setPage(pg)}
                  className={cn('w-8 h-8 text-sm rounded-lg border font-medium',
                    pg===page?'bg-brand-500 text-white border-brand-500':'border-gray-200 hover:bg-gray-50 text-gray-600')}>
                  {pg}
                </button>
              )
            })}
            <span className="text-sm text-gray-400">of {lastPage}</span>
            <button onClick={()=>setPage(p=>p+1)} disabled={page>=lastPage}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-gray-600">
              Next <ChevronRight size={14}/>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
